import { useState, type FormEvent } from 'react'
import { useNavigate } from 'react-router-dom'
import { apiErrorMessage } from '../../api/client'
import { authApi } from '../../api/endpoints'
import { useAuth } from '../../auth/AuthContext'
import { JoinCodeStep } from '../../components/auth/JoinCodeStep'
import { ChipGroup } from '../../components/ChipGroup'
import { ErrorMessage } from '../../components/ErrorMessage'
import { OnboardingShell } from '../../components/OnboardingShell'
import { fillIfEmpty, reverseGeocode } from '../../utils/reverseGeocode'
import { isValidMobile, MOBILE_PATTERN } from '../../utils/validation'
import type { InstitutionConfig } from './institutionConfig'
import type { InstitutionRegisterRequest, RegistryKind } from '../../types/api'

/** A company may quote any of three registries; a school or college has exactly one. */
const COMPANY_REGISTRIES: { kind: RegistryKind; label: string; pattern: RegExp; help: string }[] = [
  { kind: 'CIN', label: 'CIN', pattern: /^[A-Za-z0-9]{21}$/, help: 'Enter the 21-character CIN' },
  { kind: 'GSTIN', label: 'GSTIN', pattern: /^[A-Za-z0-9]{15}$/, help: 'Enter the 15-character GSTIN' },
  {
    kind: 'UDYAM',
    label: 'Udyam',
    pattern: /^UDYAM-[A-Za-z]{2}-\d{2}-\d{7}$/,
    help: 'Enter the Udyam number, for example UDYAM-UP-12-1234567',
  },
]

/**
 * A heading inside the single form, in place of a step.
 *
 * <p>This form is long enough that a flat run of twenty fields would be hard to place yourself in,
 * which is the one thing the steps were genuinely good at. A rule and a small label keep that
 * sense of where you are without gating anything behind a Continue button.
 */
function Section({ title }: { title: string }) {
  return (
    <h2 className="border-t border-ink-200 pt-4 text-[11px] font-bold uppercase tracking-wide text-ink-400 first:border-0 first:pt-0">
      {title}
    </h2>
  )
}

/**
 * Institution registration on one page, driven by {@link InstitutionConfig}.
 *
 * <p>One component for schools, colleges and companies because the specs describe the same form
 * three times, changing only labels and option lists - see institutionConfig.ts.
 *
 * <p>It was four steps, and is now one, matching what student, tutor and guardian registration
 * already do. The steps never bought anything the submit did not: everything was posted at the end
 * as a single request either way, so all they added was three clicks and a review screen listing
 * back what you had just typed. On one page the length is visible, which is honest - the sections
 * below are headings, not gates.
 */
export function InstitutionRegisterForm({
  config,
  referralCode,
  onBack,
}: {
  config: InstitutionConfig
  referralCode?: string
  onBack: () => void
}) {
  const navigate = useNavigate()
  const { verifyRegistration } = useAuth()
  const [error, setError] = useState<string | null>(null)
  const [submitting, setSubmitting] = useState(false)
  // The request a join code was sent for. Set means the code step is showing; the form state below
  // is kept, so "Change email" goes back to a filled-in form.
  const [sentBody, setSentBody] = useState<InstitutionRegisterRequest | null>(null)

  // The institution
  const [name, setName] = useState('')
  const [institutionType, setInstitutionType] = useState('')
  const [registryKind, setRegistryKind] = useState<RegistryKind>(config.registryKind)
  const [registryCode, setRegistryCode] = useState('')
  const [attributes, setAttributes] = useState<Record<string, string[]>>({})
  const [yearEstablished, setYearEstablished] = useState('')
  const [website, setWebsite] = useState('')
  const [description, setDescription] = useState('')

  // Where it is. A pincode and an area, the same two things a student or a tutor is asked for -
  // the street address, city, state and landmark this used to collect are gone. None of them
  // placed the dot (the pincode and the optional pinned point do that), so they were four required
  // -feeling fields standing between an institution and the end of a form that is already long.
  const [pincode, setPincode] = useState('')
  const [area, setArea] = useState('')
  const [coords, setCoords] = useState<{ lat: number; lng: number } | null>(null)
  const [locating, setLocating] = useState(false)

  // Who is signing up
  const [coordinatorName, setCoordinatorName] = useState('')
  const [designation, setDesignation] = useState('')
  const [phone, setPhone] = useState('')
  const [email, setEmail] = useState('')

  // Consent
  const [termsAccepted, setTermsAccepted] = useState(false)

  const isCompany = config.entityKind === 'COMPANY'
  const registry = isCompany
    ? COMPANY_REGISTRIES.find((r) => r.kind === registryKind) ?? COMPANY_REGISTRIES[0]
    : { kind: config.registryKind, label: config.registryLabel, pattern: config.registryPattern, help: config.registryHelp }

  /**
   * Pins the campus point and fills the pincode and area from it.
   *
   * <p>Reverse-geocoded through Nominatim, so no extra key or account is involved. Only empty
   * fields are filled: someone who has already typed their area should not have it overwritten by
   * whatever the browser thinks their coordinates are.
   *
   * <p>The coordinates are kept even when the lookup fails - the dot is the important half, and
   * both fields can still be typed.
   */
  function useMyLocation() {
    if (!navigator.geolocation) return
    setLocating(true)
    setError(null)
    navigator.geolocation.getCurrentPosition(
      async (pos) => {
        const { latitude, longitude } = pos.coords
        setCoords({ lat: latitude, lng: longitude })
        try {
          const found = await reverseGeocode(latitude, longitude)
          setPincode((p) => (/^\d{6}$/.test(p) ? p : found.pincode ?? ''))
          // Falls back to the city when the point has no named area, matching what student and
          // tutor registration put in the same field.
          setArea((v) => fillIfEmpty(v, found.area ?? found.city))
        } catch (err) {
          console.error('[institution-register] could not look up the location', err)
          setError('Location pinned, but the pincode could not be looked up - please type it in.')
        } finally {
          setLocating(false)
        }
      },
      () => {
        setLocating(false)
        setError('Could not get your location. Check the browser has permission, or type the pincode in.')
      },
      { timeout: 8000 },
    )
  }

  /**
   * The first thing wrong, named as the fix rather than as "invalid input".
   *
   * <p>In field order, so the message always points at the earliest thing to correct rather than
   * whichever check happens to be written first.
   */
  function validate(): string | null {
    if (!name.trim()) return `Enter the ${config.noun}'s name`
    if (!institutionType) return `Choose the ${config.typeLabel.toLowerCase()}`
    if (config.showRegistry && !registry.pattern.test(registryCode.trim())) return registry.help
    for (const f of config.attributeFields) {
      if (f.required && !(attributes[f.key]?.length)) return `Choose at least one ${f.label.toLowerCase()}`
    }
    if (config.websiteRequired && !website.trim()) {
      return 'Enter your website - the verification team checks the company against it'
    }
    if (config.showYearEstablished && yearEstablished && !/^\d{4}$/.test(yearEstablished)) {
      return 'Year established must be four digits'
    }

    if (!/^\d{6}$/.test(pincode)) return 'Enter a six-digit pincode'
    if (!area.trim()) return 'Enter the area / locality'

    if (!coordinatorName.trim()) return "Enter the coordinator's name"
    if (!isValidMobile(phone)) return 'Enter a ten-digit mobile number'
    if (!email.trim()) return 'Enter an email address'

    if (!termsAccepted) return 'Accept the platform terms to finish'
    return null
  }

  async function onSubmit(e: FormEvent) {
    e.preventDefault()
    const problem = validate()
    if (problem) {
      setError(problem)
      return
    }
    setSubmitting(true)
    setError(null)
    try {
      const body: InstitutionRegisterRequest = {
        entityKind: config.entityKind,
        name: name.trim(),
        institutionType,
        // Omitted entirely when this kind does not ask for one - the server treats a missing
        // code as "no registry", not as a blank one.
        registryKind: config.showRegistry ? registry.kind : undefined,
        registryCode: config.showRegistry ? registryCode.trim() : undefined,
        attributes,
        yearEstablished:
          config.showYearEstablished && yearEstablished ? Number(yearEstablished) : undefined,
        website: config.showWebsite ? website.trim() || undefined : undefined,
        description: config.showDescription ? description.trim() || undefined : undefined,
        pincode,
        // The other address fields are left off the request entirely rather than sent blank. The
        // server treats an absent one as "not collected", which is what it now is; a "" would be
        // stored as an empty address the admin screens would then render as a stray comma.
        area: area.trim() || undefined,
        lat: coords?.lat,
        lng: coords?.lng,
        coordinatorName: coordinatorName.trim(),
        coordinatorDesignation: designation || undefined,
        phone,
        email: email.trim(),
        termsAccepted,
        referralCode,
      }
      await authApi.registerInstitution(body)
      // Nothing is created yet - the join code emailed to the coordinator finishes it.
      setSentBody(body)
    } catch (err) {
      // No step to send them back to any more - a duplicate registry code or an email already in
      // use names a field that is on screen and editable where they already are.
      setError(apiErrorMessage(err, 'Could not complete the registration'))
    } finally {
      setSubmitting(false)
    }
  }

  /**
   * Step 2: the code creates the institution (or adds it to an existing account on this email) and
   * signs the coordinator in.
   */
  async function verify(code: string) {
    await verifyRegistration(email.trim(), code)
    navigate('/dashboard', { replace: true })
  }

  if (sentBody) {
    return (
      <OnboardingShell title={config.title}>
        <JoinCodeStep
          sentTo={sentBody.email}
          onVerify={verify}
          onResend={async () => {
            await authApi.registerInstitution(sentBody)
          }}
          onBack={() => setSentBody(null)}
          submitLabel="Create account"
          buttonClassName="btn-sun w-full"
        />
      </OnboardingShell>
    )
  }

  return (
    <OnboardingShell title={config.title}>
      <form onSubmit={onSubmit} className="space-y-4">
        <p className="text-sm text-ink-600">
          One page. You can add what you offer, and the rest of your profile, once you are in.
        </p>

        <Section title={config.detailsTitle} />
        <label className="block">
          <span className="label label-required">{config.detailsTitle.replace(' details', '')} name</span>
          <input className="input" value={name} onChange={(e) => setName(e.target.value)} />
        </label>
        <ChipGroup
          label={config.typeLabel}
          options={config.typeOptions}
          value={institutionType}
          onChange={setInstitutionType}
          required
        />
        {isCompany && config.showRegistry && (
          <ChipGroup
            label="Identifier type"
            options={COMPANY_REGISTRIES.map((r) => r.label)}
            value={registry.label}
            onChange={(l) =>
              setRegistryKind(COMPANY_REGISTRIES.find((r) => r.label === l)?.kind ?? 'CIN')
            }
          />
        )}
        {config.showRegistry && (
          <label className="block">
            <span className="label label-required">{isCompany ? registry.label : config.registryLabel}</span>
            <input
              className="input"
              value={registryCode}
              onChange={(e) => setRegistryCode(e.target.value)}
              placeholder={registry.help}
            />
            <span className="mt-1 block text-xs text-ink-400">{registry.help}</span>
          </label>
        )}
        {config.attributeFields.map((f) => (
          <ChipGroup
            key={f.key}
            label={f.label}
            options={f.options}
            values={attributes[f.key] ?? []}
            required={f.required}
            onToggle={(opt) =>
              setAttributes((a) => {
                const current = a[f.key] ?? []
                return {
                  ...a,
                  [f.key]: current.includes(opt)
                    ? current.filter((v) => v !== opt)
                    : [...current, opt],
                }
              })
            }
          />
        ))}
        {config.showYearEstablished && (
          <label className="block">
            <span className="label">Year established</span>
            <input
              className="input"
              inputMode="numeric"
              maxLength={4}
              value={yearEstablished}
              onChange={(e) => setYearEstablished(e.target.value.replace(/\D/g, ''))}
            />
          </label>
        )}
        {config.showWebsite && (
          <label className="block">
            <span className={`label ${config.websiteRequired ? 'label-required' : ''}`}>Website{config.websiteRequired ? '' : ' (optional)'}</span>
            <input className="input" value={website} onChange={(e) => setWebsite(e.target.value)} placeholder="https://" />
          </label>
        )}
        {config.showDescription && (
          <label className="block">
            <span className="label">Short description</span>
            <textarea
              className="input min-h-[90px]"
              maxLength={600}
              value={description}
              onChange={(e) => setDescription(e.target.value)}
              placeholder="Shown on your card"
            />
          </label>
        )}

        <Section title="Location" />
        {/* Side by side on anything wider than a phone, stacked below - the pairing student and
            tutor registration already use for these same two fields. */}
        <div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
          <label className="block">
            <span className="label label-required">Pincode</span>
            <input
              className="input"
              inputMode="numeric"
              maxLength={6}
              value={pincode}
              onChange={(e) => setPincode(e.target.value.replace(/\D/g, ''))}
              placeholder="201001"
            />
            <span className="mt-1 block text-xs text-ink-400">Places your dot on the map</span>
          </label>
          <label className="block">
            <span className="label label-required">Area / Locality</span>
            <input className="input" value={area} onChange={(e) => setArea(e.target.value)} />
          </label>
        </div>
        <button type="button" onClick={useMyLocation} className="btn-ghost">
          {locating ? 'Locating...' : coords ? 'Location pinned ✓' : 'Use my current location'}
        </button>
        <p className="text-xs text-ink-400">
          Pinning the exact point keeps two neighbouring {config.noun}s from landing on the same dot.
          A {config.noun}&apos;s pincode and area are public.
        </p>

        <Section title={config.coordinatorTitle} />
        <label className="block">
          <span className="label label-required">Coordinator name</span>
          <input className="input" value={coordinatorName} onChange={(e) => setCoordinatorName(e.target.value)} />
        </label>
        <ChipGroup
          label="Designation"
          options={config.designationOptions}
          value={designation}
          onChange={setDesignation}
        />
        <label className="block">
          <span className="label label-required">Mobile</span>
          <input
            className="input"
            inputMode="numeric"
            maxLength={10}
            pattern={MOBILE_PATTERN}
            value={phone}
            onChange={(e) => setPhone(e.target.value.replace(/\D/g, ''))}
          />
        </label>
        <label className="block">
          <span className="label label-required">Email</span>
          <input className="input" type="email" value={email} onChange={(e) => setEmail(e.target.value)} />
        </label>
        <p className="text-xs text-ink-400">We will email a join code here to finish signing up.</p>

        <label className="flex items-start gap-2 pt-2 text-sm text-ink-700">
          <input
            type="checkbox"
            className="mt-1"
            checked={termsAccepted}
            onChange={(e) => setTermsAccepted(e.target.checked)}
          />
          <span>
            I accept the YellowDots platform terms for institutions, and the{' '}
            <a href="/terms" target="_blank" rel="noopener noreferrer" className="font-semibold text-dot-700 underline underline-offset-2">
              Terms &amp; Conditions
            </a>{' '}
            and{' '}
            <a href="/privacy" target="_blank" rel="noopener noreferrer" className="font-semibold text-dot-700 underline underline-offset-2">
              Privacy Policy
            </a>{' '}
            of YellowDots AI. {config.consentLine}
          </span>
        </label>

        <ErrorMessage message={error} />

        <div className="flex items-center justify-between gap-3 pt-2">
          <button type="button" onClick={onBack} className="btn-ghost">
            Back
          </button>
          <button type="submit" disabled={submitting} className="btn-sun">
            {submitting ? 'Creating...' : 'Complete registration'}
          </button>
        </div>
      </form>
    </OnboardingShell>
  )
}
